import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { CategorySubItemDefault, CategorySubItemDefaultInput } from '@jobos/shared';
import { useToast } from '@/hooks/use-toast';

interface CategorySubItemDefaultsResponse {
  items: CategorySubItemDefault[];
}

interface CategorySubItemDefaultResponse {
  item: CategorySubItemDefault;
}

export const categorySubItemDefaultKeys = {
  all: ['category-sub-item-defaults'] as const,
};

export function useCategorySubItemDefaults() {
  return useQuery({
    queryKey: categorySubItemDefaultKeys.all,
    queryFn: async () => {
      const response = await apiClient.get<CategorySubItemDefaultsResponse>('/category-sub-item-defaults');
      return response.items;
    },
  });
}

export function useAddCategorySubItemDefault() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: CategorySubItemDefaultInput) => {
      const response = await apiClient.post<CategorySubItemDefaultResponse>('/category-sub-item-defaults', input);
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categorySubItemDefaultKeys.all });
      toast({ title: 'Default added' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add default.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateCategorySubItemDefault() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<{
      lineItemCategory: string;
      subItemCategoryKey: string;
      defaultHours: number | null;
      defaultRate: number | null;
      defaultRateType: 'hourly' | 'daily' | null;
      defaultAmount: number | null;
      defaultNote: string | null;
      sortOrder: number;
    }>) => {
      const response = await apiClient.put<CategorySubItemDefaultResponse>(`/category-sub-item-defaults/${id}`, data);
      return response.item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categorySubItemDefaultKeys.all });
      toast({ title: 'Default updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update default.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteCategorySubItemDefault() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/category-sub-item-defaults/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categorySubItemDefaultKeys.all });
      toast({ title: 'Default deleted' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete default.',
        variant: 'destructive',
      });
    },
  });
}
